import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { first, toNumber } from 'lodash';
import { catchError, filter, map, merge, of, switchMap, tap } from 'rxjs';

import {
  EnquiryOverviewActions,
  UpdateEnquiryActions,
} from '@lockton-apps/enquiries/action';
import { ProposalDocumentBuilderActions } from '@lockton-apps/proposal-documents/action';
import { QuoteSummaryActions } from '@lockton-apps/quotes/action';
import { ProposalDocumentsApiActions } from '@lockton-apps/shared/action/proposal-documents';
import { ProposalsApiActions } from '@lockton-apps/shared/action/proposals';
import {
  ProposalRouterSelectors,
  QuoteRouterSelectors,
  selectRouteParam,
} from '@lockton-apps/shared/data-access/router';

import { ProposalsService } from '../services/proposals.service';

@Injectable()
export class SharedProposalsEffects {
  private store = inject(Store);
  private actions$ = inject(Actions);
  private proposalsService = inject(ProposalsService);

  getProposalById$ = createEffect(() =>
    merge(
      this.actions$.pipe(
        ofType(ProposalDocumentBuilderActions.init),
        concatLatestFrom(() =>
          this.store.select(ProposalRouterSelectors.selectQueriedId)
        ),
        map(([, proposalId]) => proposalId),
        filter(Boolean)
      ),
      this.actions$.pipe(
        ofType(ProposalDocumentBuilderActions.init),
        concatLatestFrom(() => [
          this.store.select(ProposalRouterSelectors.selectQueriedId),
        ]),
        filter(([, proposalId]) => !proposalId),
        switchMap(() =>
          this.actions$.pipe(
            ofType(ProposalDocumentsApiActions.loadSuccess),
            map(({ document }) => first(document.objectData.proposalIds))
          )
        ),
        filter(Boolean)
      )
    ).pipe(
      concatLatestFrom(() => [
        this.store.select(selectRouteParam('portalId')).pipe(map(toNumber)),
        this.store.select(selectRouteParam('configId')).pipe(map(toNumber)),
        this.store.select(selectRouteParam('clientId')).pipe(map(toNumber)),
      ]),
      switchMap(([proposalId, portalId, configId, clientId]) => {
        return this.proposalsService
          .getProposalById(proposalId, portalId, configId, clientId)
          .pipe(
            map((proposal) => {
              return ProposalsApiActions.loadProposalSuccess({ proposal });
            })
          );
      })
    )
  );

  getProposalByQuoteId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        QuoteSummaryActions.init,
        EnquiryOverviewActions.init,
        UpdateEnquiryActions.init
      ),
      concatLatestFrom(() =>
        this.store.select(QuoteRouterSelectors.selectRoutedId)
      ),
      map(([, quoteId]) => quoteId),
      filter(Boolean),
      switchMap((quoteId) => {
        return this.proposalsService.getProposalByQuoteId(quoteId).pipe(
          map((proposal) => {
            return ProposalsApiActions.loadProposalSuccess({ proposal });
          }),
          catchError((error) => {
            return of(ProposalsApiActions.loadProposalFailure({ error }));
          })
        );
      })
    )
  );

  genericFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProposalsApiActions.loadProposalFailure),
        tap(({ error }) => {
          console.error(error);
        })
      ),
    { dispatch: false }
  );
}
